<template>
  <div class="container-fluid">

    <!-- Estado de carga -->
    <div v-if="loading" class="text-center py-5">
      <div class="loader-box">
        <div class="loader-3"></div>
      </div>
      <p>Cargando...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-danger">
        <h5>Error al cargar los datos</h5>
        <p>{{ error.message || 'Ocurrió un error inesperado' }}</p>
        <button class="btn btn-primary" @click="fetchDirectorio()">Reintentar</button>
      </div>
    </div>

    <!-- Contenido -->
    <div v-else>

      <!-- Buscador y filtros -->
      <div class="col-sm-12 col-lg-12 mt-3">
        <div class="card">
          <div class="card-body">
            <div class="d-flex">
              <input
                type="text"
                class="form-control me-2"
                placeholder="Describe el tipo de servicio que buscas..."
                v-model="searchQuery"
              />
              <button class="btn btn-primary">
                <i class="fa fa-search"></i> Buscar
              </button>
            </div>

            <div class="mt-3" v-if="sectores.length">
              <span class="me-2 fw-bold">Sectores:</span>
              <button
                v-for="sector in sectores"
                :key="sector.id"
                class="btn me-1 mb-1"
                :class="sectorActivo === sector.id ? 'btn-primary' : 'btn-outline-primary'"
                @click="filtrarPorSector(sector.id)"
              >
                {{ sector.nombre }}
              </button>
              <button
                v-if="sectorActivo"
                class="btn btn-outline-secondary mb-1"
                @click="filtrarPorSector(null)"
              >
                <i class="fa fa-times"></i> Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Sin resultados -->
      <div v-if="empresasFiltradas.length === 0" class="text-center py-5">
        <p class="text-muted">No se encontraron resultados para tu búsqueda.</p>
      </div>

      <!-- Grid de empresas (página actual) -->
      <div id="servicios-list" class="row starter-main">
        <div
          v-for="empresa in empresasPaginadas"
          :key="empresa.id"
          class="col-sm-4 mb-3"
        >
          <div
            class="card h-100"
            style="cursor: pointer; letter-spacing: 0px; padding: 0.10rem; transition: box-shadow 0.2s;"
            @mouseenter="e => e.currentTarget.style.boxShadow = '0 4px 24px rgba(16,46,82,0.18)'"
            @mouseleave="e => e.currentTarget.style.boxShadow = ''"
          >
            <div class="card-header p-0" style="overflow: hidden; border-radius: 15px 15px 0 0; background: #f0f2f5;">
              <img
                :src="empresa.logo_url || empresa.logo || empresa.thumbnail_url || empresa.thumbnail || empresa.imagen || defaultImage"
                style="width:100%; height:100%; object-fit:contain; display:block;"
                :alt="empresa.title || empresa.nombre"
                @error="e => { e.target.style.objectFit='cover'; e.target.style.padding='0'; }"
              />
            </div>
            <div class="card-body" style="padding: 20px;">
              <h5 class="mb-3">{{ empresa.nombre }}</h5>
              <div>
                <template v-if="empresa.servicios_ofrecidos">
                  <span
                    v-for="(svc, i) in parseRichTextItems(empresa.servicios_ofrecidos)"
                    :key="i"
                    class="badge bg-secondary me-1 mb-1"
                    style="font-size:0.75rem; white-space:normal; text-align:left; margin-left:0;"
                  >{{ svc }}</span>
                </template>
              </div>
            </div>
            <div class="card-footer" style="padding: 15px;">
              <div class="text-end">
                <router-link
                  :to="{ name: 'detalleServicio', params: { id: empresa.id } }"
                  class="btn btn-outline-primary btn-sm"
                >
                  <i class="fa fa-user"></i> Ver perfil
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Paginación -->
      <div v-if="totalPages > 1" class="row">
        <div class="col-12 d-flex justify-content-between align-items-center mt-2 mb-4 px-3">

          <span class="text-muted small">
            Mostrando {{ rangoInicio }}–{{ rangoFin }} de {{ empresasFiltradas.length }} empresas
          </span>

          <nav aria-label="Paginación del directorio">
            <ul class="pagination pagination-sm mb-0">

              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button class="page-link" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
                  &laquo;
                </button>
              </li>

              <li
                v-for="page in visiblePages"
                :key="page"
                class="page-item"
                :class="{ active: page === currentPage, disabled: page === '…' }"
              >
                <button
                  class="page-link"
                  @click="page !== '…' && goToPage(page)"
                  :disabled="page === '…'"
                >
                  {{ page }}
                </button>
              </li>

              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button class="page-link" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">
                  &raquo;
                </button>
              </li>

            </ul>
          </nav>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
import api from "@/axios";
import defaultImageSrc from "@/assets/images/verde/ecobanner.webp";
const PAGE_SIZE = 6;

export default {
  name: "ServiciosDirectorio",

  data() {
    return {
      directorios:  [],
      error:        null,
      loading:      true,
      searchQuery:  "",
      sectorActivo: null,
      currentPage:  1,
    };
  },

  computed: {
    defaultImage() {
      return defaultImageSrc;
    },
    sectores() {
      const map = new Map();
      this.directorios.forEach((e) => {
        const s = e.sector;
        if (s?.id != null && !map.has(s.id)) map.set(s.id, s);
      });
      return [...map.values()].sort((a, b) => a.nombre.localeCompare(b.nombre));
    },
    empresasFiltradas() {
      return this.directorios.filter((empresa) => {
        const matchSector = !this.sectorActivo || empresa.sector?.id === this.sectorActivo;
        const q = this.searchQuery.toLowerCase();
        const matchSearch =
          !q ||
          (empresa.title || empresa.nombre || "").toLowerCase().includes(q) ||
          (empresa.intro || empresa.descripcion || empresa.description || "").toLowerCase().includes(q) ||
          (empresa.sector?.nombre || "").toLowerCase().includes(q) ||
          (empresa.tipo_institucion?.nombre || "").toLowerCase().includes(q) ||
          (empresa.estado?.nombre || empresa.municipio || "").toLowerCase().includes(q);
        return matchSector && matchSearch;
      });
    },
    totalPages() {
      return Math.ceil(this.empresasFiltradas.length / PAGE_SIZE);
    },
    rangoInicio() {
      return (this.currentPage - 1) * PAGE_SIZE + 1;
    },
    rangoFin() {
      return Math.min(this.currentPage * PAGE_SIZE, this.empresasFiltradas.length);
    },
    empresasPaginadas() {
      const start = (this.currentPage - 1) * PAGE_SIZE;
      return this.empresasFiltradas.slice(start, start + PAGE_SIZE);
    },
    visiblePages() {
      const total = this.totalPages;
      const cur   = this.currentPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

      if (cur <= 4)          return [1, 2, 3, 4, 5, '…', total];
      if (cur >= total - 3)  return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
      return [1, '…', cur - 1, cur, cur + 1, '…', total];
    },
  },

  watch: {
    searchQuery()  { this.currentPage = 1; },
    sectorActivo() { this.currentPage = 1; },
  },

  created() {
    this.fetchDirectorio();
  },

  methods: {
    filtrarPorSector(sector) {
      this.sectorActivo = sector;
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return;
      this.currentPage = page;
      document.getElementById('servicios-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    parseRichTextItems(html) {
      if (!html) return [];
      const div = document.createElement('div');
      div.innerHTML = html;
      const lis = div.querySelectorAll('li');
      if (lis.length) return [...lis].map((el) => el.textContent.trim()).filter(Boolean);
      const ps  = div.querySelectorAll('p');
      if (ps.length)  return [...ps].map((el) => el.textContent.trim()).filter(Boolean);
      return div.textContent.split(',').map((s) => s.trim()).filter(Boolean);
    },

    async fetchDirectorio() {
      this.loading = true;
      this.error = null;
      try {
        const { data: firstData } = await api.get("directorio?limit=20&offset=0");
        const totalCount = firstData?.meta?.total_count
                        ?? firstData?.count
                        ?? firstData?.total
                        ?? 0;
        let allItems = [...(firstData?.items ?? (Array.isArray(firstData) ? firstData : []))];

        if (totalCount > allItems.length) {
          const pagesLeft = Math.ceil(totalCount / 20) - 1;
          const requests  = Array.from({ length: pagesLeft }, (_, i) =>
            api.get(`directorio?limit=20&offset=${(i + 1) * 20}`)
          );
          const responses = await Promise.all(requests);
          for (const resp of responses) {
            allItems = allItems.concat(resp.data?.items ?? []);
          }
        }

        this.directorios = allItems;
      } catch (err) {
        this.error = err;
        console.error("Error al cargar directorio:", err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.rich-text-preview {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.rich-text-preview p { margin-bottom: 0.25rem; }
.rich-text-preview h1,
.rich-text-preview h2,
.rich-text-preview h3,
.rich-text-preview h4 { font-size: 0.85rem; font-weight: 600; margin-bottom: 0.25rem; }
.rich-text-preview img { display: none; }
</style>